import { CloseOutlined, FilePdfOutlined, PaperClipOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Table,
  Typography,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { getApiError } from '../../api/client';
import { DateText, MoneyInput, MoneyText } from '../../lib/format';
import {
  addCheltuialaPayment,
  attachmentUrl,
  deleteCheltuiala,
  deleteCheltuialaAttachment,
  deleteCheltuialaPayment,
  expenseEfacturaPdfUrl,
  fetchCheltuiala,
  markCheltuialaPaid,
  markCheltuialaUnpaid,
  uploadCheltuialaAttachment,
  type ExpenseDetail,
  type ExpensePaymentItem,
} from './api';
import { ExpenseKindTag, ExpenseSourceTag, ExpenseStatusTag } from './tags';

const { Text, Title } = Typography;

interface PaymentFormValues {
  paidOn: Dayjs;
  amountRon: string;
  notes?: string;
}

/**
 * The split-view detail (right-hand panel of `CheltuieliListPage`): document
 * data, the proof attachments, and the RON allocations that decide the
 * payment status. Every mutation refreshes both the detail and the list.
 */
export function CheltuialaDetailPanel({
  id,
  onClose,
  onEdit,
}: {
  id: string;
  onClose: () => void;
  onEdit: (expense: ExpenseDetail) => void;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [form] = Form.useForm<PaymentFormValues>();

  const { data: expense, isLoading } = useQuery({
    queryKey: ['expenses', 'detail', id],
    queryFn: () => fetchCheltuiala(id),
  });

  function refresh(updated?: ExpenseDetail) {
    if (updated) queryClient.setQueryData(['expenses', 'detail', updated.id], updated);
    else queryClient.invalidateQueries({ queryKey: ['expenses', 'detail', id] });
    queryClient.invalidateQueries({ queryKey: ['expenses'] });
  }

  function reportError(error: unknown, fallback: string) {
    message.error(getApiError(error)?.message ?? fallback);
  }

  const markMutation = useMutation({
    mutationFn: (paid: boolean) => (paid ? markCheltuialaPaid(id) : markCheltuialaUnpaid(id)),
    onSuccess: (updated) => refresh(updated),
    onError: (error) => reportError(error, 'Statusul nu a putut fi schimbat.'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCheltuiala(id),
    onSuccess: () => {
      message.success('Cheltuiala a fost ștearsă.');
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      onClose();
    },
    onError: (error) => reportError(error, 'Cheltuiala nu a putut fi ștearsă.'),
  });

  const addPaymentMutation = useMutation({
    mutationFn: (values: PaymentFormValues) =>
      addCheltuialaPayment(id, {
        paidOn: values.paidOn.format('YYYY-MM-DD'),
        amountRon: values.amountRon,
        notes: values.notes?.trim() || undefined,
      }),
    onSuccess: (updated) => {
      message.success('Plata a fost înregistrată.');
      refresh(updated);
      setPaymentOpen(false);
    },
    onError: (error) => reportError(error, 'Plata nu a putut fi înregistrată.'),
  });

  const deletePaymentMutation = useMutation({
    mutationFn: (paymentId: string) => deleteCheltuialaPayment(id, paymentId),
    onSuccess: (updated) => {
      message.success('Plata a fost ștearsă.');
      refresh(updated);
    },
    onError: (error) => reportError(error, 'Plata nu a putut fi ștearsă.'),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadCheltuialaAttachment(id, file),
    onSuccess: () => {
      message.success('Fișierul a fost atașat.');
      refresh();
    },
    onError: (error) => reportError(error, 'Fișierul nu a putut fi atașat.'),
  });

  const deleteAttachmentMutation = useMutation({
    mutationFn: (attachmentId: string) => deleteCheltuialaAttachment(id, attachmentId),
    onSuccess: () => refresh(),
    onError: (error) => reportError(error, 'Fișierul nu a putut fi șters.'),
  });

  if (isLoading || !expense) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  // A PDF can be rendered by ANAF only from an e-Factura XML — present on
  // SPV-synced expenses (and any the user attached manually as .xml).
  const hasEfacturaXml = expense.attachments.some(
    (attachment) =>
      attachment.mimeType === 'application/xml' ||
      attachment.mimeType === 'text/xml' ||
      attachment.fileName.toLowerCase().endsWith('.xml'),
  );

  const paymentColumns: ColumnsType<ExpensePaymentItem> = [
    { title: 'Dată', key: 'paidOn', width: 110, render: (_, row) => <DateText value={row.paidOn} /> },
    { title: 'Sumă (RON)', key: 'amount', width: 130, align: 'right', render: (_, row) => <MoneyText value={row.amountRon} /> },
    {
      title: 'Sursă',
      key: 'source',
      ellipsis: true,
      render: (_, row) =>
        row.bankTransaction ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Extras: {row.bankTransaction.description ?? row.bankTransaction.statementSource}
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.notes ?? 'Înregistrată manual'}
          </Text>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, row) => (
        <Popconfirm
          title="Ștergi plata?"
          description={row.bankTransactionId ? 'Tranzacția bancară redevine nereconciliată.' : 'Statusul se recalculează.'}
          okText="Șterge"
          cancelText="Renunță"
          onConfirm={() => deletePaymentMutation.mutate(row.id)}
        >
          <Button size="small" danger>
            Șterge
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space size={8} wrap>
          <Title level={5} style={{ margin: 0 }}>
            {expense.docNumber ? `Doc. ${expense.docNumber}` : (expense.description ?? 'Cheltuială')}
          </Title>
          <ExpenseKindTag kind={expense.kind} />
          <ExpenseStatusTag status={expense.paymentStatus} />
          <ExpenseSourceTag source={expense.source} />
        </Space>
      }
      extra={
        <Space>
          <Button size="small" onClick={() => onEdit(expense)}>
            Editează
          </Button>
          {expense.paymentStatus === 'PAID' && expense.settled ? (
            <Button size="small" onClick={() => markMutation.mutate(false)} loading={markMutation.isPending}>
              Marchează neplătită
            </Button>
          ) : expense.paymentStatus !== 'PAID' ? (
            <Button size="small" type="primary" onClick={() => markMutation.mutate(true)} loading={markMutation.isPending}>
              Marchează plătită
            </Button>
          ) : null}
          <Popconfirm
            title="Ștergi cheltuiala?"
            description="Fișierele atașate se șterg odată cu ea. Alocările trebuie șterse mai întâi."
            okText="Șterge"
            cancelText="Renunță"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteMutation.mutate()}
          >
            <Button size="small" danger>
              Șterge
            </Button>
          </Popconfirm>
          <Button size="small" type="text" icon={<CloseOutlined />} onClick={onClose} />
        </Space>
      }
    >
      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="Furnizor" span={2}>
          {expense.supplier ? `${expense.supplier.name}${expense.supplier.cui ? ` (${expense.supplier.cui})` : ''}` : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Data documentului">
          <DateText value={expense.docDate} />
        </Descriptions.Item>
        <Descriptions.Item label="Scadență">
          <DateText value={expense.dueDate} />
        </Descriptions.Item>
        <Descriptions.Item label="Categorie">{expense.category?.name ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Valoare fără TVA / TVA">
          {expense.netAmount ? <MoneyText value={expense.netAmount} currency={expense.currency} /> : '—'}
          {' / '}
          {expense.vatAmount ? <MoneyText value={expense.vatAmount} currency={expense.currency} /> : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Total">
          <Text strong>
            <MoneyText value={expense.totalAmount} currency={expense.currency} />
          </Text>
          {expense.currency !== 'RON' ? (
            <Text type="secondary" style={{ display: 'block', fontSize: 12 }} className="num">
              ≈ <MoneyText value={expense.totalAmountRon} />
              {/* When there's a real RON payment the value IS that payment;
                  the BNR rate is only the estimate used while unpaid. */}
              {Number(expense.paidAmountRon) > 0
                ? ' (plată reală)'
                : expense.exchangeRate
                  ? ` (estimat la curs BNR ${Number(expense.exchangeRate).toFixed(4)})`
                  : ' (curs BNR indisponibil)'}
            </Text>
          ) : null}
        </Descriptions.Item>
        <Descriptions.Item label="Plătit (RON)">
          <MoneyText value={expense.paidAmountRon} />
        </Descriptions.Item>
        {expense.description ? (
          <Descriptions.Item label="Descriere" span={2}>
            {expense.description}
          </Descriptions.Item>
        ) : null}
      </Descriptions>

      <Card
        title="Fișiere atașate"
        size="small"
        style={{ marginTop: 16 }}
        extra={
          <Space>
            {hasEfacturaXml ? (
              <Button
                size="small"
                icon={<FilePdfOutlined />}
                href={expenseEfacturaPdfUrl(expense.id)}
                target="_blank"
                rel="noopener noreferrer"
              >
                PDF e-Factura
              </Button>
            ) : null}
            <Upload
              accept=".pdf,.jpg,.jpeg,.png,.webp,.xml"
              showUploadList={false}
              beforeUpload={(file) => {
                uploadMutation.mutate(file);
                return false;
              }}
            >
              <Button size="small" icon={<UploadOutlined />} loading={uploadMutation.isPending}>
                Adaugă fișier
              </Button>
            </Upload>
          </Space>
        }
      >
        {expense.attachments.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Niciun fișier atașat." />
        ) : (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            {expense.attachments.map((attachment) => (
              <Space key={attachment.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <a href={attachmentUrl(expense.id, attachment.id)} target="_blank" rel="noopener noreferrer">
                  <PaperClipOutlined /> {attachment.fileName}
                </a>
                <Popconfirm
                  title="Ștergi fișierul?"
                  okText="Șterge"
                  cancelText="Renunță"
                  onConfirm={() => deleteAttachmentMutation.mutate(attachment.id)}
                >
                  <Button size="small" type="text" danger>
                    Șterge
                  </Button>
                </Popconfirm>
              </Space>
            ))}
          </Space>
        )}
      </Card>

      <Card
        title="Plăți"
        size="small"
        style={{ marginTop: 16 }}
        extra={
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.setFieldsValue({ paidOn: dayjs(), amountRon: undefined, notes: undefined });
              setPaymentOpen(true);
            }}
          >
            Adaugă plată
          </Button>
        }
      >
        <Table<ExpensePaymentItem>
          rowKey="id"
          size="small"
          pagination={false}
          columns={paymentColumns}
          dataSource={expense.payments}
          locale={{
            emptyText: (
              <Text type="secondary">
                {expense.settled ? 'Marcată ca plătită, fără alocări din extras.' : 'Nicio plată înregistrată.'}
              </Text>
            ),
          }}
        />
      </Card>

      <Modal
        title="Adaugă plată manuală"
        open={paymentOpen}
        onCancel={() => setPaymentOpen(false)}
        onOk={() => form.submit()}
        okText="Înregistrează"
        cancelText="Renunță"
        confirmLoading={addPaymentMutation.isPending}
        forceRender
      >
        <Form<PaymentFormValues> form={form} layout="vertical" onFinish={(values) => addPaymentMutation.mutate(values)}>
          <Form.Item name="amountRon" label="Sumă (RON)" rules={[{ required: true, message: 'Suma este obligatorie.' }]}>
            <MoneyInput />
          </Form.Item>
          <Form.Item name="paidOn" label="Dată" rules={[{ required: true, message: 'Data este obligatorie.' }]}>
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="notes" label="Notă">
            <Input placeholder="ex. plată numerar, OP…" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
